<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, FormControl, Input, Modal, Option, Select, Textarea, type SelectOption } from '@ui'
import type { Colleague } from '@/api/intelligence-actions'
import type { TaskPriority } from '@/types/intelligence'
import { TASK_PRIORITIES } from '@/lib/action-text'
import DayPicker from '../DayPicker.vue'

/** What the form hands back: the follow-up as S0 §34.2 records it, the due date only if one was chosen. */
export interface AssignTaskPayload {
  title: string
  note?: string
  priority: TaskPriority
  dueDate?: string
  assignedToId: string
}

/**
 * Assign follow-up (P6 §2.2, S0 §34.2): accountable work for one active
 * member, with a priority and, only if there is one, a due date. Whom it is
 * for is required; the due date is not, and a follow-up without one is never
 * overdue. A member who cannot open Intelligence yet is offered too, and said
 * to be (Dao, 1 Oct 2026).
 */
const props = withDefaults(
  defineProps<{
    open: boolean
    colleagues: Colleague[]
    /** What it follows up, named, so the form says so. */
    context: string
    defaultTitle?: string
    /** Today in the workspace's time zone, the earliest due date. */
    today: string
    pending?: boolean
    /** The colleagues could not be loaded: the picker says so rather than look empty. */
    colleaguesFailed?: boolean
  }>(),
  { defaultTitle: '', pending: false, colleaguesFailed: false },
)
const emit = defineEmits<{ close: []; submit: [task: AssignTaskPayload] }>()
const { t } = useI18n()

const title = ref('')
const assignee = ref<string | null>(null)
const priority = ref<TaskPriority>('MEDIUM')
const dueDate = ref<string | null>(null)
const note = ref('')

watch(
  () => props.open,
  (open) => {
    if (!open) return
    title.value = props.defaultTitle
    assignee.value = null
    priority.value = 'MEDIUM'
    dueDate.value = null
    note.value = ''
  },
  { immediate: true },
)

const people = computed<SelectOption[]>(() =>
  props.colleagues.map((colleague) => ({
    value: colleague.userId,
    label: colleague.name,
    ...(colleague.hasIntelligence ? {} : { description: t('actions.form.noSeatYet') }),
  })),
)
const chosenPerson = computed(() => people.value.find((option) => option.value === assignee.value))
const choosePerson = (option: SelectOption | SelectOption[] | undefined): void => {
  if (option && !Array.isArray(option)) assignee.value = option.value
}
const withoutSeat = computed(() => props.colleagues.find((colleague) => colleague.userId === assignee.value)?.hasIntelligence === false)

const priorities = computed<SelectOption[]>(() => TASK_PRIORITIES.map((value) => ({ value, label: t(`actions.priority.${value}`) })))
const chosenPriority = computed(() => priorities.value.find((option) => option.value === priority.value))
const choosePriority = (option: SelectOption | SelectOption[] | undefined): void => {
  if (option && !Array.isArray(option)) priority.value = option.value as TaskPriority
}

const canSubmit = computed(() => title.value.trim() !== '' && assignee.value !== null)

const submit = (): void => {
  if (!canSubmit.value || !assignee.value) return
  const text = note.value.trim()
  emit('submit', {
    title: title.value.trim(),
    priority: priority.value,
    assignedToId: assignee.value,
    ...(dueDate.value ? { dueDate: dueDate.value } : {}),
    ...(text ? { note: text } : {}),
  })
}
</script>

<template>
  <Modal :model-value="open" :title="t('actions.assign.title')" size="md" type="content" no-icon @close="emit('close')">
    <template #content>
      <div class="form" data-test="assign-form">
        <p class="form__context">{{ t('actions.form.followsUp', { name: context }) }}</p>
        <FormControl name="task-title" :label="t('actions.assign.taskTitle')" required>
          <Input v-model="title" :maxlength="140" data-test="assign-title" />
        </FormControl>
        <FormControl
          name="task-assignee"
          :label="t('actions.assign.assignee')"
          :help-text="withoutSeat ? t('actions.form.noSeatHelp') : undefined"
          :error-message="colleaguesFailed ? t('actions.form.colleaguesFailed') : undefined"
          required
        >
          <Select :model-value="chosenPerson" :options="people" searchable :placeholder="t('actions.form.choosePerson')" data-test="assign-assignee" @update:model-value="choosePerson">
            <Option v-for="option in people" :key="option.value" :label="option.label" :value="option.value" :description="option.description" />
          </Select>
        </FormControl>
        <FormControl name="task-priority" :label="t('actions.assign.priority')" required>
          <Select :model-value="chosenPriority" :options="priorities" data-test="assign-priority" @update:model-value="choosePriority">
            <Option v-for="option in priorities" :key="option.value" :label="option.label" :value="option.value" />
          </Select>
        </FormControl>
        <FormControl name="task-due" :label="t('actions.assign.dueDate')" :help-text="t('actions.assign.dueDateHelp')">
          <DayPicker v-model="dueDate" :min="today" clearable data-test="assign-due" />
        </FormControl>
        <FormControl name="task-note" :label="t('actions.assign.note')">
          <Textarea v-model="note" :maxlength="2000" data-test="assign-note" />
        </FormControl>
      </div>
    </template>

    <template #actions>
      <Button type="secondary" @click="emit('close')">{{ t('common.cancel') }}</Button>
      <Button :loading="pending" :disabled="!canSubmit" :aria-disabled="!canSubmit" data-test="assign-submit" @click="submit">
        {{ t('actions.assign.submit') }}
      </Button>
    </template>
  </Modal>
</template>

<style scoped>
.form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
}

.form__context {
  margin: 0;
  font-size: var(--font-size-body-2);
  line-height: var(--line-height-body-2);
  color: var(--fg-secondary);
}
</style>
